import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsCompanyAdmin } from '../../hooks/useIsCompanyAdmin';
import { useUser } from '../../hooks/useUser';
import { ProtectedRoute } from './ProtectedRoute';

interface CompanyAdminRouteProps {
  children: ReactNode;
  /**
   * Si fourni, autoriser aussi les "nouveaux users" (sans companyId encore)
   * à accéder à la route. Utile pour la page `/company` qui sert à la fois
   * à créer une nouvelle entreprise (autorisé à tous les nouveaux) et à
   * éditer l'entreprise existante (autorisé uniquement à l'admin).
   */
  allowNewAccounts?: boolean;
}

/**
 * Route réservée à l'administrateur/propriétaire de l'entreprise.
 *
 * Layering :
 *  1. `ProtectedRoute` (auth Auth0) — interne, garantit que l'utilisateur est connecté
 *  2. Check `useIsCompanyAdmin` :
 *     - admin → on rend les enfants
 *     - non-admin AVEC company → redirection vers `/profile` (il n'a pas
 *       les droits sur la gestion entreprise / abonnements)
 *     - nouveau user SANS company → autorisé seulement si `allowNewAccounts=true`
 *       (pour la création initiale)
 *
 * Empêche un comptable, manager ou employé d'accéder à `/abonnement`,
 * `/company` (édition), etc.
 */
export function CompanyAdminRoute({
  children,
  allowNewAccounts = false,
}: CompanyAdminRouteProps) {
  return (
    <ProtectedRoute>
      <CompanyAdminGate allowNewAccounts={allowNewAccounts}>
        {children}
      </CompanyAdminGate>
    </ProtectedRoute>
  );
}

function CompanyAdminGate({
  children,
  allowNewAccounts,
}: {
  children: ReactNode;
  allowNewAccounts: boolean;
}) {
  const { isAdmin, isLoading } = useIsCompanyAdmin();
  const { user } = useUser();

  if (isLoading) return null;

  const hasCompany = !!(user?.companyId && user.companyId.trim() !== '');
  const hasFinancialInstitution = !!(
    user?.financialInstitutionId && user.financialInstitutionId.trim() !== ''
  );
  const isNewAccount = !hasCompany && !hasFinancialInstitution;

  // Admin OU propriétaire → accès direct
  if (isAdmin) return <>{children}</>;

  // Nouveau user (pas encore d'entreprise) → accès autorisé si la route le permet
  // (cas typique : `/company` pour la création initiale)
  if (allowNewAccounts && isNewAccount) return <>{children}</>;

  // User membre (sans rôle admin) avec une entreprise existante → redirection profil
  return <Navigate to="/profile" replace />;
}
